import { App, Divider, Image, Spin } from "antd";
import styles from "./styles.module.scss";
import { ReactComponent as FileIcon } from "../../assets/file.svg";
import { useMutation, useQueries } from "@tanstack/react-query";
import {
  getCouponDiscount,
  getInstitutionByApplicantId,
  getPersonalInfoByApplicantId,
  getQualificationByApplicantId,
  getUploadNyscByApplicantId,
  getWorkHistoryByApplicantId,
} from "../../utils/request";
import { useAtom } from "jotai";
import { pageIndexAtom, userAtom } from "../../utils/store";
import { formatDate } from "../../utils/formatDate";
import OnboardingButtonGroup from "./onboardingButtonGroup";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../custom";
import { routes } from "../../routes";
import { formatCurrency } from "../../utils/currency";
import { useApplicationPeriod } from "../../hooks/useApplicationPeriod";
import RegistrationForm from "./registrationForm";
import { useReactToPrint } from "react-to-print";
import { APP_BASE_URL } from "../../utils/constants";
import { usePayment } from "../../contexts/payment";

const Review = () => {
  const [user, setUser] = useAtom(userAtom);
  const [pageIndex, setPageIndex] = useAtom(pageIndexAtom);
  const {
    isLateApplicationPeriod,
    shouldPayLateApplicationFee,
    lateApplicationFee,
  } = useApplicationPeriod();
  const { initiatePayment, isProcessing } = usePayment();
  const [visible, setVisible] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [discountStatus, setDiscountStatus] = useState<
    "success" | "error" | ""
  >("");
  const printRef = useRef<HTMLDivElement>(null);

  const printDocument = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Application_Review_${user?.applicantId}`,
    pageStyle: `
    @page {
      size: A4;
      margin: 20mm;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      * {
        overflow: visible !important;
        height: auto !important;
      }
    }
  `,
  });

  const { notification } = App.useApp();

  const navigate = useNavigate();

  const queries = useQueries({
    queries: [
      {
        queryKey: ["get-institution"],
        queryFn: () => getInstitutionByApplicantId(user?.applicantId ?? ""),
      },
      {
        queryKey: ["get-work-history"],
        queryFn: () => getWorkHistoryByApplicantId(user?.applicantId ?? ""),
      },
      {
        queryKey: ["get-nysc-upload"],
        queryFn: () => getUploadNyscByApplicantId(user?.applicantId ?? ""),
      },
      {
        queryKey: ["get-personal-info-id"],
        queryFn: () => getPersonalInfoByApplicantId(user?.applicantId ?? ""),
      },
      {
        queryKey: ["get-all-qualification"],
        queryFn: () => getQualificationByApplicantId(user?.applicantId ?? ""),
      },
    ],
  });

  const institutionQuery = queries[0];
  const workHistoryQuery = queries[1];
  const nyscUploadQuery = queries[2];
  const personalInfoQuery = queries[3];
  const qualificationQuery = queries[4];

  const personalInfoData =
    personalInfoQuery?.data?.data ?? ({} as PersonalInfo);
  const institutionData = institutionQuery?.data?.data ?? [];
  const workHistoryData = workHistoryQuery?.data?.data ?? [];
  const nyscUploadData = nyscUploadQuery?.data?.data ?? ({} as NyscUpload);
  const qualificationData = qualificationQuery?.data?.data ?? [];

  const isInstitutionLoading = institutionQuery?.isLoading;
  const isWorkHistoryLoading = workHistoryQuery?.isLoading;
  const isNyscUploadLoading = nyscUploadQuery?.isLoading;

  const applyDiscountMutation = useMutation({
    mutationFn: getCouponDiscount,
  });

  const handleProcessPayment = async () => {
    let payload: ProcessPaymentPayloadWithChannel = {
      amount: 0,
      returnUrl: `${APP_BASE_URL}/payments`,
      applicantId: user?.applicantId ?? "",
      paymentType: 1,
    };
    if (couponCode) {
      payload.couponCode = couponCode;
    }
    initiatePayment(payload);
  };

  const handleApplyDiscount = async () => {
    setDiscountStatus("");
    const payload: CouponDiscountPayload = {
      couponCode: couponCode,
      applicantId: user?.applicantId ?? "",
    };
    try {
      await applyDiscountMutation?.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: "Success",
            description: data?.message,
          });
          setDiscountStatus("success");
        },
      });
    } catch (error: any) {
      setCouponCode("");
      setDiscountStatus("error");
      notification.error({
        message: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const isLateApplication = isLateApplicationPeriod();
  const discountAmount = applyDiscountMutation?.data?.data?.discountAmount ?? 0;
  const totalAmount =
    applyDiscountMutation?.data?.data?.discountedAmount ?? user?.amount ?? 0;

  const handlePrevClick = () => {
    setPageIndex(pageIndex > 1 ? pageIndex - 1 : pageIndex);
  };

  const isValidDate = (date: string | null) => {
    return date !== null && date.includes("0001-01-01") === false;
  };

  const handleExit = useCallback(() => {
    setUser({
      isNewUser: null,
      applicantId: "",
      token: "",
      programType: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneNo: "",
      applicationName: "",
      sessionId: 0,
      programTypeId: 0,
      amount: 0,
      earlyApplicationAmount: 0,
      lateApplicationAmount: 0,
    });

    localStorage.clear();

    navigate(routes.auth.landing);
  }, [navigate, setUser]);

  if (isInstitutionLoading || isWorkHistoryLoading || isNyscUploadLoading) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <section className={styles.section}>
        <h5>Review</h5>
        <p>Please review your information before you proceed</p>
        <hr />
      </section>

      <div className={styles.reviewWrapper} ref={printRef}>
        <div className={styles.summary}>
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>Application Details</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  <div className={styles.institutionReview}>
                    <div>
                      <p>Program:</p>
                      <p>{user?.programName}</p>
                    </div>
                    <div>
                      <p>Program Type:</p>
                      <p>{user?.programType}</p>
                    </div>
                    <div>
                      <p>Mode of Study:</p>
                      <p>{user?.modeOfStudyName}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <div className={styles.payment}>
            <h3>Payment Details</h3>
            {isLateApplication ? null : (
              <section>
                <div className={styles.discount}>
                  <div className={styles.paymentInput}>
                    <input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Enter Coupon Code"
                    />
                  </div>
                  <button
                    onClick={handleApplyDiscount}
                    disabled={!couponCode || applyDiscountMutation?.isPending}
                  >
                    {applyDiscountMutation?.isPending ? "Applying..." : "Apply"}
                  </button>
                </div>
                {discountStatus && (
                  <small
                    style={{
                      color: discountStatus === "success" ? "green" : "red",
                    }}
                  >
                    {discountStatus === "success"
                      ? "Discount applied"
                      : "Invalid code"}
                  </small>
                )}
              </section>
            )}
            <section className={styles.breakdown}>
              <div className={styles.breakdownItem}>
                <h6>Application Fee</h6>
                <p>{formatCurrency(user?.earlyApplicationAmount ?? 0)}</p>
              </div>
              {shouldPayLateApplicationFee && (
                <div className={styles.breakdownItem}>
                  <h6>Late Application Fee</h6>
                  <p>{formatCurrency(lateApplicationFee)}</p>
                </div>
              )}
              {discountAmount > 0 && (
                <div className={styles.breakdownItem}>
                  <h6>Discount</h6>
                  <p>- {formatCurrency(discountAmount)}</p>
                </div>
              )}
              <Divider />
              <div className={styles.breakdownItem}>
                <h6>Total</h6>
                <p>
                  {discountAmount > 0 && (
                    <s>{formatCurrency(user?.amount ?? 0)}</s>
                  )}
                  &nbsp; {formatCurrency(totalAmount ?? 0)}
                </p>
              </div>
              <Divider />
            </section>
          </div>
        </div>

        <section className={styles.reviewContainer}>
          <div>
            <div>
              <h5>1.</h5>
              <h5>Bio-data</h5>
            </div>

            <div>
              <div>
                <Divider className={styles.divider} type="vertical" />
                <Divider
                  className={styles.dashedDivider}
                  type="vertical"
                  dashed
                />
              </div>

              <div className={styles.textWrapper}>
                <div className={styles.image}>
                  <Image
                    width={120}
                    height={100}
                    src={personalInfoData?.imageUrl}
                    alt="review"
                    preview={{
                      width: "500",
                      height: "600",
                      visible,
                      scaleStep: 1,
                      src: personalInfoData?.imageUrl,
                      onVisibleChange: (value) => {
                        setVisible(value);
                      },
                    }}
                  />
                </div>

                <div>
                  <Button text="View Image" onClick={() => setVisible(true)} />
                </div>

                <div className={styles.institutionReview}>
                  <div>
                    <p>First Name:</p>
                    <p>{personalInfoData?.fName}</p>
                  </div>
                  <div>
                    <p>Middle Name:</p>
                    <p>{personalInfoData?.mNane}</p>
                  </div>
                  <div>
                    <p>Last Name:</p>
                    <p>{personalInfoData?.lName}</p>
                  </div>
                  <div>
                    <p>Gender:</p>
                    <p>{personalInfoData?.gender}</p>
                  </div>
                  <div>
                    <p>Phone No:</p>
                    <p>{personalInfoData?.phoneNo}</p>
                  </div>
                  <div>
                    <p>Email:</p>
                    <p>{personalInfoData?.email}</p>
                  </div>
                  <div>
                    <p>Address:</p>
                    <p>
                      {personalInfoData?.address} {personalInfoData?.zipCode}.
                    </p>
                  </div>
                  <div>
                    <p>Country:</p>
                    <p>{personalInfoData?.countryName}</p>
                  </div>
                  <div>
                    <p>Date Of Birth:</p>
                    <p>{formatDate(personalInfoData?.dateOfBirth)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {user && user?.programType?.toLowerCase()?.includes("msc") && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>2.</h5>
                <h5>Institution</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {institutionData?.map((item, index) => (
                    <div key={item?.id} className={styles.institutionReview}>
                      <div>
                        <p>Institution Attended:</p>
                        <p>{item?.institutionName}</p>
                      </div>

                      <div>
                        <p>Discipline:</p>
                        <p>{item?.discipline}</p>
                      </div>

                      <div>
                        <p>Year(start date):</p>
                        <p>{formatDate(item?.startDate)}</p>
                      </div>

                      <div>
                        <p>Year(end date):</p>
                        <p>{formatDate(item?.endDate)}</p>
                      </div>

                      <div>
                        <p>Grade:</p>
                        <p>{item?.cgpa}/5.0</p>
                      </div>

                      <div className={styles.docStyle}>
                        <FileIcon />
                        <a
                          href={item?.certificateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.certificateLink}
                        >
                          View Certificate
                        </a>
                      </div>

                      {index + 1 < institutionData?.length && <hr />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {user && user?.programType?.toLowerCase()?.includes("bsc") && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>2.</h5>
                <h5>Qualification</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  {qualificationData &&
                    qualificationData?.map((item, index) => (
                      <div key={item?.id} className={styles.institutionReview}>
                        <div>
                          <p>Qualification Type:</p>
                          <p>{item?.qualificationType}</p>
                        </div>

                        <div>
                          <p>Exam Number:</p>
                          <p>{item?.examNumber}</p>
                        </div>

                        <div className={styles.qualification}>
                          <p>O'level Result:</p>

                          <div className={styles.subject}>
                            {item?.subjects?.map((subject) => (
                              <div>
                                <div>
                                  <p>{subject?.subject}</p>
                                  <p>{subject?.grade}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className={styles.docStyle}>
                          <FileIcon />

                          <a
                            href={item?.certificateiUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.certificateLink}
                          >
                            View Certificate
                          </a>
                        </div>

                        {index + 1 < qualificationData?.length && <hr />}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </section>
        )}

        <section className={styles.reviewContainer}>
          <div>
            <div>
              <h5>3.</h5>
              <h5>Work History</h5>
            </div>

            <div>
              <div>
                <Divider className={styles.divider} type="vertical" />
                <Divider
                  className={styles.dashedDivider}
                  type="vertical"
                  dashed
                />
              </div>

              <div className={styles.textWrapper}>
                {workHistoryData?.map((item, index) => (
                  <div key={item?.id} className={styles.institutionReview}>
                    <div>
                      <p>Company:</p>
                      <p>{item?.companyName}</p>
                    </div>
                    <div>
                      <p>Role:</p>
                      <p>{item?.position}</p>
                    </div>
                    <div>
                      <p>Duration:</p>
                      <p>{`${formatDate(item?.startDate)} - ${
                        isValidDate(item?.endDate)
                          ? formatDate(item?.endDate)
                          : "Present"
                      }`}</p>
                    </div>

                    {index + 1 < workHistoryData?.length && <hr />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {Object.keys(nyscUploadData)?.length > 0 && (
          <section className={styles.reviewContainer}>
            <div>
              <div>
                <h5>4.</h5>
                <h5>Document Upload</h5>
              </div>

              <div>
                <div>
                  <Divider className={styles.divider} type="vertical" />
                  <Divider
                    className={styles.dashedDivider}
                    type="vertical"
                    dashed
                  />
                </div>

                <div className={styles.textWrapper}>
                  <div className={styles.docStyle}>
                    <p>NYSC Document</p>

                    <FileIcon />

                    <a
                      href={nyscUploadData?.nyscUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.certificateLink}
                    >
                      View Certificate
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      <RegistrationForm
        personalInfo={personalInfoData}
        institutions={institutionData}
        workHistory={workHistoryData}
        nyscUpload={nyscUploadData}
        qualifications={qualificationData}
        printRef={printRef}
        shouldPayLateApplicationFee={shouldPayLateApplicationFee}
        lateApplicationFee={lateApplicationFee}
        discountAmount={discountAmount}
        totalAmount={totalAmount}
      />

      <OnboardingButtonGroup
        onClick={handlePrevClick}
        onNextClick={handleProcessPayment}
        onExit={handleExit}
        isLoading={isProcessing}
        isReview={!user?.isPaymentSubmitted}
        onPrint={printDocument}
        hasPaid={user?.isPaymentSubmitted}
      />
    </div>
  );
};

export default Review;
